import React, { useEffect, useMemo, useState } from "react";
import { PhoneCall, RotateCcw, UserX, CheckCircle2, ArrowRightLeft, Clock } from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useTenantSocket } from "../lib/ws";

const STATUS_STYLES = {
  waiting: "bg-slate-100 text-slate-700",
  called: "bg-blue-100 text-blue-800",
  done: "bg-emerald-100 text-emerald-800",
  absent: "bg-rose-100 text-rose-700",
};

export default function Staff() {
  const { tenant } = useAuth();
  const { t, lang } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [counters, setCounters] = useState([]);
  const [services, setServices] = useState([]);
  const [counterId, setCounterId] = useState(localStorage.getItem("filepro_counter") || "");
  const [transferTarget, setTransferTarget] = useState({});
  const [err, setErr] = useState("");

  const load = async () => {
    const [tk, me] = await Promise.all([
      api.get("/tickets"),
      api.get("/tenant/me"),
    ]);
    setTickets(tk.data.tickets);
    setCounters(me.data.counters);
    setServices(me.data.services);
    // If the stored counter id doesn't belong to the current tenant's counters
    // (typical when switching between clinics), reset to the first available one.
    const validIds = new Set(me.data.counters.map((c) => c.id));
    if (!counterId || !validIds.has(counterId)) {
      const first = me.data.counters[0];
      setCounterId(first ? first.id : "");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useTenantSocket(tenant?.slug, (msg) => {
    if (!msg || !msg.type) return;
    if (msg.type.startsWith("ticket.")) load();
  });

  useEffect(() => {
    if (counterId) localStorage.setItem("filepro_counter", counterId);
  }, [counterId]);

  const waiting = tickets.filter((t) => t.status === "waiting");
  const called = tickets.filter((t) => t.status === "called");
  const done = tickets.filter((t) => t.status === "done");
  const absent = tickets.filter((t) => t.status === "absent");

  const myCalled = called.filter((x) => x.counter_id === counterId);

  const act = async (fn) => {
    setErr("");
    try { await fn(); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  const callNext = () => act(async () => {
    if (!counterId) throw new Error("Sélectionnez une salle");
    await api.post("/tickets/call-next", { counter_id: counterId });
  });
  const recall = (id) => act(() => api.post("/tickets/recall", { ticket_id: id }));
  const markAbsent = (id) => act(() => api.post("/tickets/mark-absent", { ticket_id: id }));
  const complete = (id) => act(() => api.post("/tickets/complete", { ticket_id: id }));
  const transfer = (id, targetId) => act(() => api.post("/tickets/transfer", { ticket_id: id, target_service_id: targetId }));

  const currentCounter = useMemo(() => counters.find((c) => c.id === counterId), [counters, counterId]);

  return (
    <div className="min-h-screen">
      <AppHeader activeItem="staff" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-blue-700 font-medium">{t("staff")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">{tenant?.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-600">{t("select_counter")}</div>
            <select
              data-testid="staff-counter-select"
              value={counterId}
              onChange={(e) => setCounterId(e.target.value)}
              className="px-4 py-2 rounded-full border border-slate-200 bg-white font-medium text-slate-900"
            >
              {counters.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={callNext}
              disabled={!counterId || waiting.length === 0}
              data-testid="staff-call-next-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50"
            >
              <PhoneCall className="w-4 h-4" /> {t("call_next")}
            </button>
          </div>
        </div>

        {err && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="staff-error">{err}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: t("waiting"), count: waiting.length, tone: "bg-slate-50 text-slate-900 border-slate-200" },
            { label: t("called"), count: called.length, tone: "bg-blue-50 text-blue-900 border-blue-100" },
            { label: t("done"), count: done.length, tone: "bg-emerald-50 text-emerald-900 border-emerald-100" },
            { label: t("absent"), count: absent.length, tone: "bg-rose-50 text-rose-900 border-rose-100" },
          ].map((s) => (
            <div key={s.label} className={`rounded-2xl border p-4 ${s.tone}`}>
              <div className="text-xs uppercase tracking-[0.2em]">{s.label}</div>
              <div className="font-heading text-3xl font-bold mt-1">{s.count}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <section className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-heading text-xl font-semibold text-slate-900">{t("current_patient")} · {currentCounter?.name || "—"}</h2>
            {myCalled.length === 0 ? (
              <div className="mt-6 text-slate-500 text-sm">—</div>
            ) : (
              myCalled.slice(0, 2).map((c) => (
                <div key={c.id} className="mt-4 p-5 rounded-2xl border border-blue-200 bg-blue-50">
                  <div className="font-heading text-6xl font-bold text-blue-900">{c.number}</div>
                  <div className="mt-1 text-blue-800">{lang === "fr" ? c.service_name_fr : c.service_name_en}</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => recall(c.id)} data-testid={`staff-recall-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-slate-200 text-sm hover:bg-slate-50">
                      <RotateCcw className="w-4 h-4" /> {t("recall")}
                    </button>
                    <button onClick={() => markAbsent(c.id)} data-testid={`staff-absent-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-rose-200 text-rose-700 text-sm hover:bg-rose-50">
                      <UserX className="w-4 h-4" /> {t("mark_absent")}
                    </button>
                    <button onClick={() => complete(c.id)} data-testid={`staff-complete-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700">
                      <CheckCircle2 className="w-4 h-4" /> {t("complete")}
                    </button>
                    <div className="inline-flex items-center gap-2 bg-white rounded-full border border-slate-200 px-2 py-1">
                      <ArrowRightLeft className="w-4 h-4 text-slate-500" />
                      <select
                        data-testid={`staff-transfer-select-${c.id}`}
                        value={transferTarget[c.id] || ""}
                        onChange={(e) => setTransferTarget({ ...transferTarget, [c.id]: e.target.value })}
                        className="text-sm bg-transparent outline-none"
                      >
                        <option value="">{t("transfer")}…</option>
                        {services.filter((s) => s.id !== c.service_id).map((s) => (
                          <option key={s.id} value={s.id}>{lang === "fr" ? s.name_fr : s.name_en}</option>
                        ))}
                      </select>
                      <button
                        disabled={!transferTarget[c.id]}
                        onClick={() => { transfer(c.id, transferTarget[c.id]); setTransferTarget({ ...transferTarget, [c.id]: "" }); }}
                        data-testid={`staff-transfer-confirm-${c.id}`}
                        className="text-sm text-blue-700 disabled:opacity-40"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>

          <section className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-slate-900">{t("waiting_list")}</h2>
              <div className="text-sm text-slate-500">{waiting.length} {t("patients")}</div>
            </div>
            <div className="mt-4 divide-y divide-slate-100" data-testid="staff-waiting-list">
              {waiting.length === 0 && <div className="py-6 text-slate-500 text-sm">{t("no_patient_waiting")}</div>}
              {waiting.map((w) => (
                <div key={w.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`font-heading text-2xl font-bold ${w.service_prefix === "U" ? "text-rose-700" : "text-slate-900"}`}>{w.number}</div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{lang === "fr" ? w.service_name_fr : w.service_name_en}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(w.created_at).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {w.service_prefix === "U" && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 uppercase tracking-wider">{t("urgent")}</span>
                    )}
                  </div>
                  <div className={`px-2 py-1 text-xs rounded-full ${STATUS_STYLES.waiting}`}>{t("waiting")}</div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{t("called")} · {t("done")} · {t("absent")}</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="py-2">#</th>
                  <th className="py-2">{t("services")}</th>
                  <th className="py-2">{t("room")}</th>
                  <th className="py-2">{t("status")}</th>
                  <th className="py-2">{t("time")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...called, ...done, ...absent].sort((a,b)=> (b.called_at||b.created_at).localeCompare(a.called_at||a.created_at)).slice(0, 20).map((x) => (
                  <tr key={x.id} className="hover:bg-slate-50">
                    <td className="py-2 font-heading font-semibold text-slate-900">{x.number}</td>
                    <td className="py-2 text-slate-700">{lang === "fr" ? x.service_name_fr : x.service_name_en}</td>
                    <td className="py-2 text-slate-700">{x.counter_name || "—"}</td>
                    <td className="py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[x.status]}`}>{t(x.status)}</span></td>
                    <td className="py-2 text-slate-500">{x.called_at ? new Date(x.called_at).toLocaleTimeString() : new Date(x.created_at).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
